import { InputOTP, InputOTPGroup, InputOTPSlot } from "@nimit9/signet-ui/components/input-otp"
import { REGEXP_ONLY_DIGITS } from "input-otp"

const PIN_LENGTH = 6

/**
 * Why a new PIN is too easy to guess, or null if it's fine. Six digits is only
 * a million options, so refuse the handful people try first (UAT J9-02).
 */
export function weakPinReason(pin: string): string | null {
  if (/^(\d)\1{5}$/.test(pin)) return "Avoid repeating one digit. Pick a less obvious PIN."
  const digits = [...pin].map(Number)
  const step = digits[1] - digits[0]
  if ((step === 1 || step === -1) && digits.every((d, i) => i === 0 || d - digits[i - 1] === step))
    return "Avoid counting up or down. Pick a less obvious PIN."
  return null
}

/**
 * 6-digit PIN field on ShadCN's InputOTP. Same controlled contract as the old
 * six-box input (`value` / `onChange` / `autoFocus`), so every caller — the PIN
 * gate, onboarding's private-mode step and Settings › Privacy — keeps working
 * unchanged. Purely presentational: it never stores the PIN anywhere; the
 * value lives only in the caller's React state.
 *
 * One real <input> sits behind the slots, so autofill, paste ("123456", or
 * "123 456" — non-digits are stripped) and the numeric keypad all work.
 */
export function PinInput({
  value,
  onChange,
  autoFocus,
  invalid,
  disabled,
  "aria-label": ariaLabel = "6-digit PIN",
}: {
  value: string
  onChange: (v: string) => void
  autoFocus?: boolean
  /** Paint the slots in the error state (e.g. after a wrong PIN). */
  invalid?: boolean
  disabled?: boolean
  "aria-label"?: string
}) {
  return (
    <InputOTP
      maxLength={PIN_LENGTH}
      value={value}
      onChange={(v) => onChange(v.replace(/\D/g, "").slice(0, PIN_LENGTH))}
      pattern={REGEXP_ONLY_DIGITS}
      pasteTransformer={(text) => text.replace(/\D/g, "").slice(0, PIN_LENGTH)}
      inputMode="numeric"
      autoComplete="one-time-code"
      autoFocus={autoFocus}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-invalid={invalid || undefined}
      containerClassName="justify-center"
    >
      <InputOTPGroup className="gap-2">
        {Array.from({ length: PIN_LENGTH }).map((_, idx) => (
          <InputOTPSlot
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed slot positions
            key={idx}
            index={idx}
            aria-invalid={invalid || undefined}
            // Digits show as dots so the PIN can't be read off the screen (UAT J9-02).
            className="[-webkit-text-security:disc] h-12 w-10 rounded-lg sm:w-11 border border-border-default bg-bg-elevated font-mono text-subhead font-semibold text-text-primary first:rounded-lg last:rounded-lg"
          />
        ))}
      </InputOTPGroup>
    </InputOTP>
  )
}
